import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getOcrSettings,
  updateOcrSettings,
  OcrSettingsView,
  OcrSettingsInput,
} from '../../../api/General/ocrSettings';
import StickyActionBar from '../../../components/ui/StickyActionBar';
import { PageLayout } from '../../../components/ui/PageLayout';
import {
  ScanLine,
  ToggleLeft,
  ToggleRight,
  Gauge,
  FileSearch,
  CalendarClock,
  Loader2,
  CheckCircle,
  AlertCircle,
  Info,
} from 'lucide-react';

const OcrSettings: React.FC = () => {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<OcrSettingsView | null>(null);
  const [formValues, setFormValues] = useState<OcrSettingsInput | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getOcrSettings();
      if (result.errors.length === 0) {
        setSettings(result.payload);
        setFormValues({
          ocrEnabled: result.payload.ocrEnabled,
          confidenceThreshold: result.payload.confidenceThreshold,
          autoDetectDocumentType: result.payload.autoDetectDocumentType,
          autoDetectExpiryDate: result.payload.autoDetectExpiryDate,
        });
      } else {
        showToast('error', t('ocrFailedToLoadSettings'));
      }
    } catch {
      showToast('error', t('ocrFailedToLoadSettings'));
    } finally {
      setIsLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasUnsavedChanges = (): boolean => {
    if (!settings || !formValues) return false;
    return (
      settings.ocrEnabled !== formValues.ocrEnabled ||
      settings.confidenceThreshold !== formValues.confidenceThreshold ||
      settings.autoDetectDocumentType !== formValues.autoDetectDocumentType ||
      settings.autoDetectExpiryDate !== formValues.autoDetectExpiryDate
    );
  };

  const handleSave = async () => {
    if (!formValues) return;
    setIsSaving(true);
    try {
      const result = await updateOcrSettings(formValues);
      if (result.errors.length === 0) {
        setSettings(result.payload);
        setFormValues({
          ocrEnabled: result.payload.ocrEnabled,
          confidenceThreshold: result.payload.confidenceThreshold,
          autoDetectDocumentType: result.payload.autoDetectDocumentType,
          autoDetectExpiryDate: result.payload.autoDetectExpiryDate,
        });
        showToast('success', t('ocrSettingsSaved'));
      } else {
        showToast('error', t('ocrFailedToSaveSettings'));
      }
    } catch {
      showToast('error', t('ocrFailedToSaveSettings'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (!settings) return;
    setFormValues({
      ocrEnabled: settings.ocrEnabled,
      confidenceThreshold: settings.confidenceThreshold,
      autoDetectDocumentType: settings.autoDetectDocumentType,
      autoDetectExpiryDate: settings.autoDetectExpiryDate,
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('Never');
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <PageLayout
      title={t('ocrSettings')}
      description={t('ocrSettingsDescription')}
      icon={ScanLine}
      accentColor="slate"
    >
      <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
            toast.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {toast.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Audit Info */}
      {settings?.lastModifiedBy && (
        <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <Info className="w-4 h-4 flex-shrink-0" />
          <span>
            {t('Last modified by')} <strong>{settings.lastModifiedBy}</strong> {t('on')}{' '}
            {formatDate(settings.lastModifiedAt)}
          </span>
        </div>
      )}

      {/* Settings Card */}
      <div className="bg-card rounded-xl border border-border shadow-sm divide-y divide-border">
        {formValues && (
          <>
            {/* OCR Enabled Toggle */}
            <div className="flex items-center justify-between px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600">
                  <ScanLine className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-800">{t('ocrEnableOcr')}</span>
                  <p className="text-xs text-gray-500">{t('ocrEnableOcrDescription')}</p>
                </div>
              </div>
              <button
                onClick={() => setFormValues(prev => prev ? { ...prev, ocrEnabled: !prev.ocrEnabled } : prev)}
                className="text-gray-600 hover:text-blue-600 transition-colors"
              >
                {formValues.ocrEnabled ? (
                  <ToggleRight className="w-10 h-10 text-blue-600" />
                ) : (
                  <ToggleLeft className="w-10 h-10 text-gray-400" />
                )}
              </button>
            </div>

            {/* Confidence Threshold */}
            <div className="flex items-center justify-between px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600">
                  <Gauge className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-800">{t('ocrConfidenceThreshold')}</span>
                  <p className="text-xs text-gray-500">{t('ocrConfidenceThresholdDescription')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={100}
                  value={formValues.confidenceThreshold}
                  onChange={(e) =>
                    setFormValues(prev => prev ? { ...prev, confidenceThreshold: parseInt(e.target.value) } : prev)
                  }
                  className="w-32 accent-blue-600"
                />
                <span className="text-sm font-mono font-medium text-gray-700 w-10 text-right">
                  {formValues.confidenceThreshold}%
                </span>
              </div>
            </div>

            {/* Auto Detect Document Type */}
            <div className="flex items-center justify-between px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-100 text-purple-600">
                  <FileSearch className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-800">{t('ocrAutoDetectType')}</span>
                  <p className="text-xs text-gray-500">{t('ocrAutoDetectTypeDescription')}</p>
                </div>
              </div>
              <button
                onClick={() =>
                  setFormValues(prev =>
                    prev ? { ...prev, autoDetectDocumentType: !prev.autoDetectDocumentType } : prev
                  )
                }
                className="text-gray-600 hover:text-purple-600 transition-colors"
              >
                {formValues.autoDetectDocumentType ? (
                  <ToggleRight className="w-10 h-10 text-purple-600" />
                ) : (
                  <ToggleLeft className="w-10 h-10 text-gray-400" />
                )}
              </button>
            </div>

            {/* Auto Detect Expiry Date */}
            <div className="flex items-center justify-between px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 text-green-600">
                  <CalendarClock className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-800">{t('ocrAutoDetectExpiry')}</span>
                  <p className="text-xs text-gray-500">{t('ocrAutoDetectExpiryDescription')}</p>
                </div>
              </div>
              <button
                onClick={() =>
                  setFormValues(prev =>
                    prev ? { ...prev, autoDetectExpiryDate: !prev.autoDetectExpiryDate } : prev
                  )
                }
                className="text-gray-600 hover:text-green-600 transition-colors"
              >
                {formValues.autoDetectExpiryDate ? (
                  <ToggleRight className="w-10 h-10 text-green-600" />
                ) : (
                  <ToggleLeft className="w-10 h-10 text-gray-400" />
                )}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Sticky Action Bar */}
      <StickyActionBar
        hasUnsavedChanges={hasUnsavedChanges()}
        isSaving={isSaving}
        onReset={handleReset}
        onSave={handleSave}
        t={t}
      />
      </div>
    </PageLayout>
  );
};

export default OcrSettings;
